import React from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';

interface Series {
  videoId: string;
  title: string;
  color: string;
  counts: number[];
}

interface BookingsLineChartProps {
  dates: string[];
  series: Series[];
  title?: string;
}

const formatDateLabel = (d: string) => {
  const dt = new Date(d + 'T00:00:00Z');
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const BookingsLineChart: React.FC<BookingsLineChartProps> = ({ dates, series, title }) => {
  if (dates.length === 0 || series.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
        No bookings data available for the selected range.
      </div>
    );
  }

  const chartData = dates.map((date, i) => {
    const row: Record<string, number | string> = { date, label: formatDateLabel(date) };
    series.forEach(s => {
      row[s.videoId] = s.counts[i] || 0;
    });
    return row;
  });

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: 0,
      width: '100%'
    }}>
      {title && (
        <h3 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#111827', textAlign: 'center' }}>
          {title}
        </h3>
      )}
      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer>
          <AreaChart data={chartData} margin={{ top: 10, right: 24, left: 0, bottom: 8 }}>
            <defs>
              {series.map(s => (
                <linearGradient key={`grad-${s.videoId}`} id={`grad-${s.videoId}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.85} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.55} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickLine={false}
              axisLine={{ stroke: '#E5E7EB' }}
              minTickGap={24}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickLine={false}
              axisLine={{ stroke: '#E5E7EB' }}
              width={36}
            />
            <Tooltip
              cursor={{ stroke: '#9CA3AF', strokeDasharray: '4 4' }}
              contentStyle={{
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                border: 'none',
                borderRadius: 8,
                color: '#F9FAFB',
                fontSize: 12,
                padding: '10px 12px'
              }}
              labelStyle={{ color: '#F9FAFB', fontWeight: 600, marginBottom: 4 }}
              itemStyle={{ color: '#F9FAFB' }}
              formatter={(value, name) => {
                const s = series.find(ss => ss.videoId === String(name));
                return [value as number, s?.title || String(name)];
              }}
            />
            {series.map(s => (
              <Area
                key={s.videoId}
                type="monotone"
                dataKey={s.videoId}
                stackId="bookings"
                stroke={s.color}
                strokeWidth={1.5}
                fill={`url(#grad-${s.videoId})`}
                isAnimationActive
                animationDuration={600}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BookingsLineChart;
